import { useEffect, useMemo, useState } from 'react';
import { INcsScoresReport, INcsScoresReportTotals } from '@nkr/models/index';
import { GridWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import {
    IHeaderParams, GridReadyEvent,
    ValueGetterParams, FirstDataRenderedEvent, ICellRendererParams, SortChangedEvent, GridSizeChangedEvent, ColDef, ColSpanParams, RowDataChangedEvent, ExcelStyle,
    GridApi,
    ColumnApi
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Glyphicon, OverlayTrigger } from 'react-bootstrap';
import PopoverTemplate, { PopoverWrapper } from '@nkr/components/popover-template';
import { useMediaQuery } from 'react-responsive';
import HeaderHoverOver from './header-hover-over';
import { NCSGridHeader } from './ncs-grid-headers';

interface IProps {
    data: INcsScoresReport[],
    totals: INcsScoresReportTotals[],
    isAdmin: boolean,
    centerId: number,
    centerFilterType: number,
    onGridReady: (e: GridReadyEvent) => void,
    onClickHardToPlace: (centerId: number) => void
}

const SummaryGrid = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);

    const isBigScreen = useMediaQuery({ minWidth: 1920 });

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    useEffect(() => {
        if (columnApi) {
            columnApi.setColumnVisible("isNkrParticipant", props.centerFilterType == 3);
        }
    }, [props.centerFilterType, columnApi]);

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        let columnsToShow = [];
        let columnsToHide = [];

        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];

            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        e.api.sizeColumnsToFit();
    };

    const [columnDefs, setColumnDefs] = useState<ColDef[]>([
        {
            colId: '1',
            field: 'rank',
            headerName: 'Rank',
            headerComponentFramework: (props: IHeaderParams) =>
                <div>R#</div>,
            sortable: false,
            width: 50,
            pinned: 'left',
            hide: !props.isAdmin,
            cellRendererSelector: (selectorParams: any) => {
                if (selectorParams.node.rowPinned) {
                    return {
                        frameworkComponent: (params: ICellRendererParams) => null
                    };
                }

                return {
                    frameworkComponent: (params: ICellRendererParams) => <>{params.value}</>
                };
            },
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            cellClass: 'center-list-cell',
            headerClass: 'center-list-header',
            hide: !props.isAdmin,
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Center
                </SortableHeaderComponent>,
            width: 150,
            colSpan: (params: ColSpanParams) => {
                let data = params.data as INcsScoresReport;

                if (data.centerId != 0) {
                    return 1;
                } else {
                    return 1;
                }
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as INcsScoresReport;

                if (data.centerId != 0) {
                    return params.value;
                } else {
                    return 'TOTALS';
                }
            }
        },
        {
            colId: 'isNkrParticipant',
            field: 'isNkrParticipant',
            headerName: 'Member Center',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Member<br />Center
                </SortableHeaderComponent>,
            width: 60,
            sortingOrder: ['desc', null],
            cellRendererSelector: (selectorParams: any) => {
                if (selectorParams.node.rowPinned) {
                    return {
                        frameworkComponent: (params: ICellRendererParams) => {
                            let data = selectorParams.data as INcsScoresReportTotals;
                            return <>{data.memberCenterCount}</>;
                        },
                    };
                }

                return {
                    frameworkComponent: (params: ICellRendererParams) => {
                        return params.value ? <Glyphicon glyph='ok' style={{ fontSize: 14 }} /> : null;
                    }
                };
            },
        },
        {
            colId: 'isAdpPartner',
            field: 'isAdpPartner',
            headerName: 'Partner Center',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.PartnerCenter} {...params} >
                    Partner<br />Center
                </HeaderHoverOver>,
            width: 60,
            sortingOrder: ['desc', 'asc', null],
            cellRendererSelector: (selectorParams: any) => {
                if (selectorParams.node.rowPinned) {
                    return {
                        frameworkComponent: (params: ICellRendererParams) => {
                            let data = selectorParams.data as INcsScoresReportTotals;
                            return <>{data.partnerCenterCount}</>;
                        },
                    };
                }

                return {
                    frameworkComponent: (params: ICellRendererParams) => {
                        return params.value ? <Glyphicon glyph='ok' style={{ fontSize: 14 }} /> : null;
                    }
                };
            },
        },
        {
            colId: 'ncSu',
            field: 'ncSu',
            headerName: 'Net Chains Started Usable',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.NetChainsStartedUsable} {...params}>
                    Net Chains<br />Started<br />Usable
                </HeaderHoverOver>,
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <OverlayTrigger trigger={['hover', 'hover']} overlay={<PopoverTemplate dismiss={false}>
                        <table>
                            <tbody>
                                <tr>
                                    <td style={{ paddingRight: 20 }}>NCSa:</td>
                                    <td style={{ textAlign: 'right' }}>{params.data.summaryNCSa}</td>
                                </tr>
                                <tr>
                                    <td style={{ paddingRight: 20 }}>NCSf:</td>
                                    <td style={{ textAlign: 'right' }}>{params.data.ncSb}</td>
                                </tr>
                            </tbody>
                        </table>
                    </PopoverTemplate>}>
                        <div>{params.value}</div>
                    </OverlayTrigger>
                );
            }
        },
        {
            colId: 'fvCume',
            field: 'fvCume',
            headerName: 'Unredeemed Family Voucher Donations',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.UnredeemedFVDonations} {...params}>
                    Unredeemed<br />Family Voucher<br />Donations
                </HeaderHoverOver>,
            width: 95,
        },
        {
            colId: 'ncsDeficit',
            field: 'ncsDeficit',
            headerName: 'Net Chains Started Score',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.NetChainStartedScore} {...params}>
                    Net Chains<br />Started<br />Score
                </HeaderHoverOver>,
            width: 70,
        },

        {
            colId: 'anyAnatomy',
            field: 'anyAnatomy',
            headerName: 'Center Accepts Any Functional Anatomy',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.CenterAcceptsAnyAnatomy} {...params}>
                    Center<br />Accepts<br />Any Functional Anatomy
                </HeaderHoverOver>,
            width: 120,
            sortingOrder: ['desc', 'asc', null],
            cellRendererSelector: (selectorParams: any) => {
                if (selectorParams.node.rowPinned) {
                    return {
                        frameworkComponent: (params: ICellRendererParams) => {
                            let data = selectorParams.data as INcsScoresReportTotals;
                            return <>{data.anyAnatomyCount}</>;
                        },
                    };
                }

                return {
                    frameworkComponent: (params: ICellRendererParams) => {
                        return params.value ? <Glyphicon glyph='ok' style={{ fontSize: 14 }} /> : <Glyphicon glyph='remove' style={{ fontSize: 14, color: "red" }} title={params.data.anatomyRestrictions} />;
                    }
                };
            },
        },

        {
            colId: 'declineRatio',
            field: 'declineRatio',
            headerName: 'WL Decline Ratio',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.WaitListDeclineRatio} {...params}>
                    WL<br />Decline<br />Ratio
                </HeaderHoverOver>,
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                return (
                    <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={<PopoverTemplate dismiss={false}>
                        <table>
                            <tbody>
                                <tr>
                                    <td style={{ paddingRight: 20 }}>WL Declines Last 6 Months:</td>
                                    <td style={{ textAlign: 'right' }}>{params.data.wlpDeclines}</td>
                                </tr>
                                <tr>
                                    <td style={{ paddingRight: 20 }}>WL Offers Last 6 Months:</td>
                                    <td style={{ textAlign: 'right' }}>{params.data.wlpOffers}</td>
                                </tr>
                            </tbody>
                        </table>
                    </PopoverTemplate>}>
                        <div>{params.value}</div>
                    </OverlayTrigger>
                );
            }
        },

        {
            colId: 'csfvStartersLast24',
            field: 'csfvStartersLast24',
            headerName: 'Family Voucher Donations Last 24 Months',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.FVDonations} {...params}>
                    Family Voucher<br />Donations<br />Last 24 Months
                </HeaderHoverOver>,
            width: 105,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'cssvStartersLast24',
            field: 'cssvStartersLast24',
            headerName: 'Standard Voucher Donations Last 24 Months',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.SVDonations} {...params}>
                    Standard Voucher<br />Donations<br />Last 24 Months
                </HeaderHoverOver>,
            width: 115,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'csCompPairsLast24',
            field: 'csCompPairsLast24',
            headerName: 'Compatibles Last 24 Months',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.CompatiblePairTxps} {...params}>
                    Compatibles<br />Last 24 Months
                </HeaderHoverOver>,
            width: 115,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'csRemoteSVOrPDLast24',
            field: 'csRemoteSVOrPDLast24',
            headerName: 'Remote Compatible Donors (Standard Voucher or Paired) Last 24 Months',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.CompatibleRemoteSVOrPaired} {...params}>
                    Remote Compatible Donors<br />(Standard Voucher or Paired)<br />Last 24 Months
                </HeaderHoverOver>,
            width: 170,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'anyAnatomyDeclineRatioBonus',
            field: 'anyAnatomyDeclineRatioBonus',
            headerName: 'Any Functional Anatomy + Zero Decline Bonus',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.AnyAnatomyAndZeroDecline} {...params}>
                    Any Functional<br />Anatomy +<br />Zero Decline<br />Bonus
                </HeaderHoverOver>,
            width: 115,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'compScore',
            field: 'compScore',
            headerName: 'Composite Score',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.CompositeScore} {...params}>
                    Composite<br />Score
                </HeaderHoverOver>,
            width: 75,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'shareOfCompScore',
            field: 'shareOfCompScore',
            headerName: 'Composite Score %',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.CompositeScorePercent} {...params}>
                    Composite<br />Score %
                </HeaderHoverOver>,
            width: 75,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'shareOfFloat',
            field: 'shareOfFloat',
            headerName: 'Share of Float',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.ShareOfFloat} {...params}>
                    Share of<br />Float
                </HeaderHoverOver>,
            width: 70,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'donorAcceptanceBonus',
            field: 'donorAcceptanceBonus',
            headerName: 'Hard to Place Donor Add Backs',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.DonorAcceptanceBonus} {...params}>
                    Hard to Place<br />Donor<br />Add Backs
                </HeaderHoverOver>,
            width: 95,
            sortingOrder: ['desc', 'asc', null],
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as INcsScoresReport;

                return (
                    <a onClick={() => props.onClickHardToPlace(data.centerId)}>{data.donorAcceptanceBonus}</a>
                )
            }
        },

        {
            colId: 'nfvTarget',
            field: 'nfvTarget',
            headerName: 'Targeted Chain End',
            headerComponentFramework: (params: IHeaderParams) =>
                <HeaderHoverOver header={NCSGridHeader.TargetedChainEnds} {...params}>
                    <span>Targeted<br />Chain<br />End</span>
                </HeaderHoverOver>,
            width: 70,
            sortingOrder: ['desc', 'asc', null],
        },

        {
            colId: 'rank',
            field: 'rank',
            hide: true
        },
    ]);

    const excelStyles: ExcelStyle[] = useMemo<ExcelStyle[]>(() => {
        return [
            {
                id: 'header',
                alignment: {
                    horizontal: 'Center',
                    wrapText: true
                },
                font: {
                    bold: true
                }
            },
            {
                id: 'cell',
                alignment: {
                    horizontal: 'Center'
                }
            }
        ];
    }, []);

    const rowData = useMemo(() => props.data, [props.data]);
    const totalsData = useMemo(() => props.totals, [props.totals]);

    return (
        <>
            <div>
                {props.isAdmin && (
                    <h3>NCS Summary Report</h3>
                )}

                <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' height={!props.isAdmin ? '12vh' : null} fontSize={!props.isAdmin ? '14px' : null} style={{ marginBottom: 20 }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        defaultColDef={{
                            cellClass: 'center-list-cell',
                            headerClass: 'center-list-header',
                            sortable: true,
                            resizable: true
                        }}
                        rowData={props.isAdmin ? rowData : rowData.filter(x => x.centerId == props.centerId)}
                        pinnedBottomRowData={props.isAdmin ? totalsData : []}
                        headerHeight={props.isAdmin ? 75 : 73}
                        enableCellTextSelection={true}
                        enableRangeSelection={true}
                        suppressCellSelection={true}
                        suppressContextMenu={true}
                        excelStyles={excelStyles}
                        noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                        onGridReady={onGridReady}
                        onGridSizeChanged={onGridSizeChanged}
                        onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                        onSortChanged={(e: SortChangedEvent) => {
                            if (e.columnApi.getColumnState().filter(x => x.sort != null).length == 0) {
                                let cs = e.columnApi.getColumnState();
                                let s = cs.find(x => x.colId == 'rank');
                                s.sort = 'asc';

                                e.columnApi.applyColumnState({ state: cs });
                            }

                            e.api.refreshCells();
                        }}
                        getRowClass={(params: any) => {
                            let data = params.data as INcsScoresReport;

                            var className = "";

                            if (props.isAdmin) {
                                if (data.summaryNCSa < 0 && data.ncSb < 0 && data.isAdpPartner == false) {
                                    className = "bg-danger";
                                }

                                if (data.summaryNCSa >= 0 && data.ncSb < 0 && data.isAdpPartner == false) {
                                    className = "bg-danger";
                                }

                                if (data.ncSu == 0 && data.isAdpPartner == false) {
                                    className = "";
                                }

                                if (data.summaryNCSa < 0 && data.ncSb == 0 && data.isAdpPartner == false) {
                                    className = "";
                                }

                                if ((data.ncSu > 0 || data.declineRatio > 30) && data.isAdpPartner == true) {
                                    className = "bg-default";
                                }

                                if (data.declineRatio <= 30 && data.isAdpPartner == true) {
                                    if (data.nfvTarget < 0) {
                                        className = "bg-slate";
                                    } else {
                                        className = "bg-info";
                                    }
                                }

                                if (data.ncSu > 0 && data.declineRatio <= 30) {
                                    className = "bg-success";
                                }

                                if (data.isNkrParticipant == false) {
                                    className = "bg-warning";
                                }
                                return className;
                            } else {
                                if (data.centerId == props.centerId) {
                                    className = "bg-warning";
                                }
                            }

                            if (data.centerId == 0) {
                                return "bg-info";
                            }
                        }}
                    />
                </GridWrapper>

                {!props.isAdmin && (
                    <h4>* Only Partner Centers will have a Composite Score & Targeted Chain End Number</h4>
                )}
            </div>
        </>
    );
}

export default SummaryGrid;